import React, { useContext, useEffect, useMemo, useState } from "react";
import { onSnapshot, query, updateDoc, addDoc } from "firebase/firestore";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  FormControl,
  IconButton,
  ImageList,
  ImageListItem,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircle as CheckCircleIcon,
  Replay as ReplayIcon,
  Build as BuildIcon,
  Phone as PhoneIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { createMeta, completeMeta, updateMeta } from "../utils/auditFields";
import { propertyDoc, propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIORITIES = ["low", "medium", "high"];
const CATEGORIES = [
  "General",
  "Plumbing",
  "Electrical",
  "HVAC",
  "Appliance",
  "Carpentry",
  "Pest Control",
  "Other",
];
const AGING_DAYS = 3; // open orders older than this are flagged as aging
const COL_COUNT = 8;

// ── People helpers ────────────────────────────────────────────────────────────
// The canonical assignment id is `userId || id` (matches the dispatch / turn-
// history convention). When a technician is tied to a user account we assign by
// their auth uid so the order shows up in their maintenance app.

const assignId = (person) => person?.userId || person?.id;

const isMaintenance = (person) =>
  (person?.type || person?.userType || "").toLowerCase() === "maintenance";

// Find a person from an order's stored `assignedTo`, which may hold either an
// auth uid or a raw employee-doc id depending on how the order was created.
const resolvePerson = (people, value) =>
  value ? people.find((p) => p.userId === value || p.id === value) || null : null;

const resolveName = (people, value) => {
  if (!value) return "";
  const person = resolvePerson(people, value);
  if (person?.name) return person.name;
  // Unresolved: hide raw uids (long, no spaces) rather than show an id.
  return value.length > 20 && !value.includes(" ") ? "Unknown" : value;
};

// ── Time helpers ──────────────────────────────────────────────────────────────

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const date = toDate(value);
  return date
    ? date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
};

const daysOldNumber = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
};

const ageLabel = (value) => {
  const days = daysOldNumber(value);
  if (days === null) return "—";
  if (days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
};

// ── Status helpers ────────────────────────────────────────────────────────────

const priorityChipColor = (priority) => {
  switch ((priority || "").toLowerCase()) {
    case "high":
      return "error";
    case "low":
      return "default";
    default:
      return "warning";
  }
};

const statusOf = (order) => {
  if (order.cancelled) return "Cancelled";
  if (order.completed) return "Completed";
  if (order.markedCompleted) return "Pending Approval";
  if (!order.assignedTo) return "Unassigned";
  return "Assigned";
};

const statusChipColor = (status) => {
  switch (status) {
    case "Completed":
      return "success";
    case "Pending Approval":
      return "primary";
    case "Assigned":
      return "info";
    case "Cancelled":
      return "default";
    default:
      return "warning"; // Unassigned
  }
};

const hasNotes = (order) => (order.notes?.length || 0) > 0;

// A work order may only be *completed* once it has an assignee, field notes, and
// has been marked complete in the field. Anything else must be Cancelled instead.
const canApprove = (order) =>
  !!order.assignedTo && hasNotes(order) && order.markedCompleted === true;

// ── New Work Order modal ──────────────────────────────────────────────────────

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: 480 },
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
};

const WOModal = ({
  open,
  onClose,
  rooms,
  maintenanceOptions,
  userInfo,
  clientId,
  selectedProperty,
}) => {
  const [roomObj, setRoomObj] = useState(null);
  const [priority, setPriority] = useState("medium");
  const [category, setCategory] = useState("General");
  const [approvedPTE, setApprovedPTE] = useState(false);
  const [assignedTo, setAssignedTo] = useState(""); // holds assignId (userId || id)
  const [phone, setPhone] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const sortedRooms = useMemo(
    () =>
      [...rooms].sort((a, b) =>
        String(a.displayName || "").localeCompare(String(b.displayName || ""), undefined, {
          numeric: true,
        })
      ),
    [rooms]
  );

  const reset = () => {
    setRoomObj(null);
    setPriority("medium");
    setCategory("General");
    setApprovedPTE(false);
    setAssignedTo("");
    setPhone("");
    setDesc("");
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!roomObj || !desc.trim()) return;
    setSaving(true);
    try {
      const workOrdersRef = propertyCollection(clientId, selectedProperty, "workOrders");
      const assignee = maintenanceOptions.find((p) => assignId(p) === assignedTo);
      await addDoc(workOrdersRef, {
        completed: false,
        cancelled: false,
        isSignedFor: false,
        markedCompleteBy: null,
        markedCompleteByName: null,
        markedCompleted: false,
        markedCompletedTime: null,
        room: roomObj.displayName,
        pteApproved: approvedPTE,
        orderDetails: desc.trim(),
        status: "pending",
        phoneNumber: phone.trim() || null,
        receiveUpdates: false,
        reportedTime: new Date(),
        assignedTo: assignedTo || null,
        assignedToName: assignee?.name || null,
        assignedTime: assignedTo ? new Date() : null,
        category,
        priority,
        costActual: null,
        ...createMeta(userInfo),
      });
      reset();
      onClose();
    } catch (err) {
      console.error("Failed to create work order:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleCancel}>
      <Box sx={modalStyle}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          New Work Order
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <Autocomplete
            size="small"
            options={sortedRooms}
            value={roomObj}
            onChange={(e, val) => setRoomObj(val)}
            getOptionLabel={(o) => String(o.displayName ?? "")}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label="Room #" />}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="wo-priority-label">Priority</InputLabel>
              <Select
                labelId="wo-priority-label"
                label="Priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="wo-category-label">Category</InputLabel>
              <Select
                labelId="wo-category-label"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="wo-assign-label">Assign to</InputLabel>
              <Select
                labelId="wo-assign-label"
                label="Assign to"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {maintenanceOptions.map((p) => (
                  <MenuItem key={assignId(p)} value={assignId(p)}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="wo-pte-label">PTE Approved</InputLabel>
              <Select
                labelId="wo-pte-label"
                label="PTE Approved"
                value={approvedPTE}
                onChange={(e) => setApprovedPTE(e.target.value)}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Guest phone (optional)"
            size="small"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="For status updates"
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !roomObj || !desc.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Creating…" : "Create Work Order"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// ── Expandable detail row ─────────────────────────────────────────────────────

const WorkOrderRow = ({
  order,
  people,
  maintenanceOptions,
  open,
  onToggle,
  onAssign,
  onPriority,
  onApprove,
  onReopen,
  onCancel,
  busy,
}) => {
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState("");

  const status = statusOf(order);
  const isOpen = status === "Unassigned" || status === "Assigned";
  const isPending = status === "Pending Approval";
  const isCompleted = status === "Completed";
  const isCancelled = status === "Cancelled";
  const isActive = isOpen || isPending;
  const aging = isOpen && (daysOldNumber(order.reportedTime) || 0) >= AGING_DAYS;
  const approvable = canApprove(order);

  const notes = order.notes || [];
  const images = order.completionImages || [];

  // Resolve the assignee to a name + a dropdown value that matches an option.
  const assignedPerson = resolvePerson(people, order.assignedTo);
  const selectedVal = assignedPerson ? assignId(assignedPerson) : order.assignedTo || "";
  const assignedName =
    order.assignedToName || assignedPerson?.name || (order.assignedTo ? "Assigned" : "—");

  // Ensure the current assignee is always selectable even if not in the
  // maintenance list (e.g. reassigned historically or account since removed).
  const rowOptions = useMemo(() => {
    const opts = [...maintenanceOptions];
    if (order.assignedTo && !opts.some((o) => assignId(o) === selectedVal)) {
      opts.unshift(
        assignedPerson || { id: order.assignedTo, name: order.assignedToName || "Assigned" }
      );
    }
    return opts;
  }, [maintenanceOptions, order.assignedTo, order.assignedToName, selectedVal, assignedPerson]);

  const creator = resolveName(people, order.createdBy);

  const missing = [];
  if (!order.assignedTo) missing.push("an assignee");
  if (!hasNotes(order)) missing.push("field notes");

  const submitCancel = () => {
    onCancel(order, reason.trim());
    setShowCancel(false);
    setReason("");
  };

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={() => onToggle(order.id)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={status} color={statusChipColor(status)} size="small" />
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{order.room || "—"}</TableCell>
        <TableCell
          sx={{
            maxWidth: 280,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {order.orderDetails || "—"}
        </TableCell>
        <TableCell>
          {order.priority ? (
            <Chip
              label={order.priority}
              color={priorityChipColor(order.priority)}
              size="small"
              variant="outlined"
              sx={{ textTransform: "capitalize" }}
            />
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell>{assignedName}</TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{ color: aging ? "error.main" : "text.primary", fontWeight: aging ? 600 : 400 }}
          >
            {ageLabel(order.reportedTime)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {isPending ? (
            <Tooltip title={approvable ? "Approve & close" : `Needs ${missing.join(" and ")}`}>
              <span>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => onApprove(order)}
                  disabled={busy || !approvable}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : isCompleted ? (
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(order.completedAt || order.markedCompletedTime)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={COL_COUNT}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                divider={<Divider orientation="vertical" flexItem />}
              >
                {/* Left: details + meta + manager controls */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                    {order.orderDetails || "No description provided."}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                    <Chip size="small" variant="outlined" label={`Category: ${order.category || "General"}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={order.pteApproved ? "info" : "default"}
                      label={order.pteApproved ? "PTE Approved" : "PTE Not Approved"}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Reported ${formatDateTime(order.reportedTime)}`}
                    />
                    {creator && <Chip size="small" variant="outlined" label={`By ${creator}`} />}
                  </Stack>

                  {order.phoneNumber && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">{order.phoneNumber}</Typography>
                    </Stack>
                  )}

                  {isActive && (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id={`assign-${order.id}`}>Assigned to</InputLabel>
                        <Select
                          labelId={`assign-${order.id}`}
                          label="Assigned to"
                          value={selectedVal}
                          onChange={(e) => onAssign(order, e.target.value)}
                          disabled={busy}
                        >
                          <MenuItem value="">
                            <em>Unassigned</em>
                          </MenuItem>
                          {rowOptions.map((p) => (
                            <MenuItem key={assignId(p)} value={assignId(p)}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id={`prio-${order.id}`}>Priority</InputLabel>
                        <Select
                          labelId={`prio-${order.id}`}
                          label="Priority"
                          value={order.priority || "medium"}
                          onChange={(e) => onPriority(order, e.target.value)}
                          disabled={busy}
                        >
                          {PRIORITIES.map((p) => (
                            <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>
                              {p}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Approval / completion gating */}
                  {isPending && !approvable && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Can't approve yet — this work order still needs {missing.join(" and ")}. Send it
                      back to the crew, or cancel &amp; close it if it won't be completed.
                    </Alert>
                  )}
                  {isOpen && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Not completed in the field yet. Assign it and let the crew finish, or cancel
                      &amp; close it if it won't be worked.
                    </Alert>
                  )}

                  {/* Actions */}
                  {isActive && !showCancel && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                      {isPending && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => onApprove(order)}
                          disabled={busy || !approvable}
                        >
                          Approve &amp; Close
                        </Button>
                      )}
                      {isPending && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReplayIcon />}
                          onClick={() => onReopen(order)}
                          disabled={busy}
                        >
                          Send Back
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => setShowCancel(true)}
                        disabled={busy}
                      >
                        Cancel &amp; Close
                      </Button>
                    </Stack>
                  )}

                  {isActive && showCancel && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Reason (optional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this being closed without completion?"
                        sx={{ mb: 1 }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={submitCancel}
                          disabled={busy}
                        >
                          Confirm Cancel
                        </Button>
                        <Button size="small" onClick={() => setShowCancel(false)} disabled={busy}>
                          Back
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  {isCompleted && (
                    <Typography variant="body2" color="text.secondary">
                      Completed by {order.completedBy || order.markedCompleteByName || "—"} on{" "}
                      {formatDateTime(order.completedAt || order.markedCompletedTime)}
                    </Typography>
                  )}
                  {isCancelled && (
                    <Typography variant="body2" color="text.secondary">
                      Cancelled{order.cancelledReason ? ` — "${order.cancelledReason}"` : ""} by{" "}
                      {order.completedBy || "—"} on {formatDateTime(order.completedAt)}
                    </Typography>
                  )}
                </Box>

                {/* Right: notes + completion photos from the field */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Field Notes
                  </Typography>
                  {notes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No notes from the field yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {notes.map((note, idx) => (
                        <Box key={idx}>
                          <Typography variant="body2">{note.text}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {note.by || "Unknown"} · {formatDateTime(note.at)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Completion Photos
                  </Typography>
                  {images.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No photos uploaded.
                    </Typography>
                  ) : (
                    <ImageList cols={3} gap={8} sx={{ mt: 0 }}>
                      {images.map((img, idx) => (
                        <ImageListItem key={idx}>
                          <a href={img.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={img.url}
                              alt={img.name || `completion-${idx}`}
                              loading="lazy"
                              style={{ borderRadius: 4 }}
                            />
                          </a>
                        </ImageListItem>
                      ))}
                    </ImageList>
                  )}
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({ value, label, color }) => (
  <Box sx={{ flex: 1, p: 2, bgcolor: `${color}.lighter`, borderRadius: 1, textAlign: "center" }}>
    <Typography variant="h4" color={`${color}.main`}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const FILTERS = ["Open", "Unassigned", "Pending Approval", "Completed", "All"];

const WorkOrders = ({ userInfo, staff: staffProp }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  const [rooms, setRooms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Open");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!selectedProperty) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);

    const roomsRef = propertyCollection(selectedClient, selectedProperty, "units");
    const workOrdersRef = propertyCollection(selectedClient, selectedProperty, "workOrders");
    const employeesRef = propertyCollection(selectedClient, selectedProperty, "employees");

    const unsubRooms = onSnapshot(query(roomsRef), (snap) =>
      setRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubOrders = onSnapshot(query(workOrdersRef), (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubEmployees = onSnapshot(query(employeesRef), (snap) =>
      setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubRooms();
      unsubOrders();
      unsubEmployees();
    };
  }, [selectedProperty, selectedClient]);

  // Merged people list used for resolving assignee / creator names. Combines the
  // `employees` collection with the `Staff` list App already provides.
  const people = useMemo(() => {
    const map = new Map();
    [...employees, ...(staffProp || [])].forEach((p) => {
      if (!map.has(p.id)) map.set(p.id, p);
    });
    return [...map.values()];
  }, [employees, staffProp]);

  // Maintenance technicians for the assignment dropdowns. Prefer the `employees`
  // collection (carries `type`); fall back to Staff if a property only has that.
  const maintenanceOptions = useMemo(() => {
    const source = employees.length ? employees : staffProp || [];
    const named = source.filter((p) => p.name);
    const maint = named.filter(isMaintenance);
    return (maint.length ? maint : named)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, staffProp]);

  // ── Manager actions ─────────────────────────────────────────────────────────

  const orderRef = (id) => propertyDoc(selectedClient, selectedProperty, "workOrders", id);

  const handleAssign = async (order, value) => {
    setBusyId(order.id);
    try {
      const assignee = maintenanceOptions.find((p) => assignId(p) === value);
      await updateDoc(orderRef(order.id), {
        assignedTo: value || null,
        assignedToName: assignee?.name || null,
        assignedTime: value ? new Date() : null,
        ...updateMeta(userInfo),
      });
    } catch (err) {
      console.error("Failed to assign work order:", err);
    } finally {
      setBusyId(null);
    }
  };

  const handlePriority = async (order, priority) => {
    setBusyId(order.id);
    try {
      await updateDoc(orderRef(order.id), { priority, ...updateMeta(userInfo) });
    } catch (err) {
      console.error("Failed to update priority:", err);
    } finally {
      setBusyId(null);
    }
  };

  const creditCompleter = async (order) => {
    const workerId = order.markedCompleteBy || order.assignedTo;
    if (!workerId) return;
    const worker = employees.find((e) => e.userId === workerId || e.id === workerId);
    if (!worker) return;
    const current = Number(worker.workOrders) || 0;
    await updateDoc(propertyDoc(selectedClient, selectedProperty, "employees", worker.id), {
      workOrders: current + 1,
    });
  };

  // Approve a field-submitted order. Guarded: only reachable when canApprove().
  const handleApprove = async (order) => {
    if (!canApprove(order)) return;
    setBusyId(order.id);
    try {
      await updateDoc(orderRef(order.id), {
        completed: true,
        status: "completed",
        ...completeMeta(userInfo),
      });
      await creditCompleter(order);
    } catch (err) {
      console.error("Failed to approve work order:", err);
    } finally {
      setBusyId(null);
    }
  };

  // Close a work order without completion (missing info, duplicate, etc.).
  const handleCancel = async (order, reason) => {
    setBusyId(order.id);
    try {
      await updateDoc(orderRef(order.id), {
        cancelled: true,
        completed: true, // removes it from open/field queues
        status: "cancelled",
        cancelledReason: reason || null,
        ...completeMeta(userInfo),
      });
    } catch (err) {
      console.error("Failed to cancel work order:", err);
    } finally {
      setBusyId(null);
    }
  };

  // Send a pending-approval order back to the field.
  const handleReopen = async (order) => {
    setBusyId(order.id);
    try {
      await updateDoc(orderRef(order.id), {
        completed: false,
        markedCompleted: false,
        markedCompletedTime: null,
        status: "pending",
        ...updateMeta(userInfo),
      });
    } catch (err) {
      console.error("Failed to send work order back:", err);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  // ── Derived data ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let open = 0;
    let unassigned = 0;
    let pending = 0;
    let aging = 0;
    orders.forEach((o) => {
      const s = statusOf(o);
      if (s === "Unassigned" || s === "Assigned") {
        open += 1;
        if (s === "Unassigned") unassigned += 1;
        if ((daysOldNumber(o.reportedTime) || 0) >= AGING_DAYS) aging += 1;
      } else if (s === "Pending Approval") {
        pending += 1;
      }
    });
    return { open, unassigned, pending, aging };
  }, [orders]);

  const visibleOrders = useMemo(() => {
    let list = orders;

    if (statusFilter === "Open") {
      list = list.filter((o) => ["Unassigned", "Assigned"].includes(statusOf(o)));
    } else if (statusFilter === "Unassigned") {
      list = list.filter((o) => statusOf(o) === "Unassigned");
    } else if (statusFilter === "Pending Approval") {
      list = list.filter((o) => statusOf(o) === "Pending Approval");
    } else if (statusFilter === "Completed") {
      list = list.filter((o) => statusOf(o) === "Completed");
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(
        (o) =>
          String(o.room || "").toLowerCase().includes(term) ||
          (o.orderDetails || "").toLowerCase().includes(term) ||
          (o.assignedToName || "").toLowerCase().includes(term) ||
          (o.category || "").toLowerCase().includes(term)
      );
    }

    return [...list].sort(
      (a, b) => (toDate(b.reportedTime)?.getTime() || 0) - (toDate(a.reportedTime)?.getTime() || 0)
    );
  }, [orders, statusFilter, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, searchTerm]);

  const pagedOrders = visibleOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!selectedProperty) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Please select a property to view work orders.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h4">Work Orders</Typography>
          <Typography variant="body2" color="text.secondary">
            Track, assign, and approve maintenance requests across the property.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<BuildIcon />} onClick={() => setModalOpen(true)}>
          New Work Order
        </Button>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard value={stats.open} label="Open" color="primary" />
        <StatCard value={stats.unassigned} label="Unassigned" color="warning" />
        <StatCard value={stats.pending} label="Pending Approval" color="info" />
        <StatCard value={stats.aging} label={`Aging (${AGING_DAYS}+ days)`} color="error" />
      </Box>

      {/* Search + Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Room, details, assignee, or category"
          sx={{ flex: 1, minWidth: 250 }}
        />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {FILTERS.map((f) => (
            <Chip
              key={f}
              label={f}
              color={statusFilter === f ? "primary" : "default"}
              onClick={() => setStatusFilter(f)}
              clickable
            />
          ))}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Status</TableCell>
              <TableCell>Room</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Age</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : pagedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 6 }}>
                  <BuildIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No work orders match this view.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedOrders.map((order) => (
                <WorkOrderRow
                  key={order.id}
                  order={order}
                  people={people}
                  maintenanceOptions={maintenanceOptions}
                  open={openId === order.id}
                  onToggle={handleToggle}
                  onAssign={handleAssign}
                  onPriority={handlePriority}
                  onApprove={handleApprove}
                  onReopen={handleReopen}
                  onCancel={handleCancel}
                  busy={busyId === order.id}
                />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={visibleOrders.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <WOModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        rooms={rooms}
        maintenanceOptions={maintenanceOptions}
        userInfo={userInfo}
        clientId={selectedClient}
        selectedProperty={selectedProperty}
      />
    </Container>
  );
};

export default WorkOrders;
